import { createContext, useEffect } from "react";

import type { Time } from "~/tools/date-time/time";
import { type Impulse, useScoped } from "~/tools/impulse";
import { type UseNowOptions, useNow } from "~/tools/use-now";
import { useRequiredContext } from "~/tools/use-required-context";

import type { SpecificRange, TimeRange } from "../../app/state/view/timeline/TimeRange";

import type { FormatTimeParams } from "./_TimelineControls";
import type { TimelineEventBucketsState } from "./_TimelineEventBuckets";
import type { TimelineLineState } from "./_TimelinePopover";

export interface TimelineContextValue {
	frozen: boolean;
	timestamp: undefined | Time;
	timeRange: TimeRange;
	frozenTimeRange: Impulse<SpecificRange>;
	eventBucketsState: TimelineEventBucketsState;
	lineState: TimelineLineState;
	timestampCursor: Impulse<undefined | Time>;
	stageWidth: number;
	refreshInterval: number;
	formatTimestamp: (params: FormatTimeParams, px: number) => string;
}

const TimelineContext = createContext<null | TimelineContextValue>(null);

export const TimelineContextProvider = TimelineContext.Provider;

export type TimelineProviderProps = Pick<
	TimelineContextValue,
	"frozen" | "timestamp" | "timeRange" | "stageWidth" | "eventBucketsState" | "lineState" | "formatTimestamp"
>;

export const useTimelineContext = (): TimelineContextValue =>
	useRequiredContext(TimelineContext, {
		providerName: "Timeline",
	});

export const useTimelineWidth = (): number => useTimelineContext().stageWidth;

export interface UseTimelineRangeOptions extends Pick<UseNowOptions, "interval" | "pause" | "floor"> {
	/**
	 * `play` is opposite of `pause` - the resulting pause == `!play && pause`
	 *
	 * @default false
	 */
	play?: boolean;
}

export const useUpdateFrozenTimeRangeOnChange = (deps: ReadonlyArray<unknown>): void => {
	const { timeRange, frozenTimeRange } = useTimelineContext();

	useEffect(() => {
		frozenTimeRange.setValue(timeRange.toSpecific());
	}, [timeRange, frozenTimeRange, ...deps]);
};

export const useTimelineRange = ({
	interval,
	play = false,
	pause = false,
	floor = "milliseconds",
}: UseTimelineRangeOptions = {}): [intervalStart: Time, intervalEnd: Time, now: Time] => {
	const { frozen, timeRange, frozenTimeRange, refreshInterval } = useTimelineContext();

	const resultingTimeRange = useScoped(
		(scope) => (frozen && !timeRange.isSpecific() ? frozenTimeRange.getValue(scope) : timeRange),
		[frozen, frozenTimeRange, timeRange],
	);

	return useNow({
		pause: !play && (resultingTimeRange.isSpecific() || pause),
		floor,
		interval: interval ?? refreshInterval,
		transform: (now) => [resultingTimeRange.start(now), resultingTimeRange.endDate(now), now],
	});
};
